import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1781513144825-aa1e284c5950?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODUxNjE1OTl8MA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="line-mask">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="visible"
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ivory grain-overlay"
    >
      {/* Ambient emerald + champagne colour wash */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute -top-52 -left-40 w-[620px] h-[620px] rounded-full bg-champagne/25 blur-[130px]" />
        <div className="absolute -bottom-40 left-1/4 w-[520px] h-[520px] rounded-full bg-emerald/10 blur-[140px]" />
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-emerald/[0.06] to-transparent" />
      </div>

      {/* Parallax image panel on the right */}
      <motion.div
        style={{ y: imgY }}
        className="absolute top-0 right-0 h-full w-full md:w-[42%] overflow-hidden"
      >
        <img
          src={HERO_IMG}
          alt="Modern luxury clinic interior"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/95 via-ivory/85 to-ivory/55 md:bg-gradient-to-r md:from-ivory md:via-ivory/10 md:to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="flex items-center gap-4 mb-8"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-champagne" />
          <p className="text-champagne-dark text-xs md:text-sm uppercase tracking-[0.35em]">
            Consultant Prosthodontist &amp; Implantologist
          </p>
        </motion.div>

        <h1 className="font-display font-light tracking-tight text-emerald text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
          <MaskLine i={0}>Restoring smiles</MaskLine>
          <MaskLine i={1}>
            with <span className="italic text-champagne-dark">precision</span>
          </MaskLine>
          <MaskLine i={2}>&amp; artistry.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 max-w-xl text-base md:text-lg text-emerald/70 leading-relaxed"
          data-testid="hero-subtext"
        >
          Dr. Priya H. Mishra delivers advanced prosthodontic and implant care — blending
          evidence-based dentistry with a refined esthetic eye for patients and referring
          clinicians across the Mumbai Metropolitan Region.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId("contact")}
            className="w-full sm:w-auto bg-emerald text-ivory px-8 py-4 uppercase tracking-[0.15em] text-sm hover:bg-emerald-light transition-colors duration-300"
          >
            Request a Consultation
          </button>
          <button
            data-testid="hero-cta-cases"
            onClick={() => scrollToId("cases")}
            className="w-full sm:w-auto border border-champagne text-emerald px-8 py-4 uppercase tracking-[0.15em] text-sm hover:bg-champagne transition-colors duration-300"
          >
            View Clinical Cases
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-3 text-emerald/50"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={18} />
        </motion.div>
        <span className="text-xs uppercase tracking-[0.25em]">Scroll to explore</span>
      </motion.div>
    </section>
  );
};

export default Hero;
